import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { convertV3CalendarEventToV2Appointment } from '@frontend/schedule-helpers';
import { useCalendarLocationIds } from '../stores/calendar-location.store';
import { ExtendedAppointment } from '../types';
import { useGetFilteredCalendarEventsV3 } from './scheduler-v3/use-get-calendar-v3-queries-data';
import { useGetFilteredAppointmentsV2 } from './use-get-calendar-v2-queries-data';

// Safe wrappers for hooks that use useCalendarStore internally
const useSafeFilteredCalendarEventsV3 = (locationIds: string[]) => {
  try {
    // TODO: pass appointment types in 2nd param
    return useGetFilteredCalendarEventsV3(locationIds);
  } catch {
    return { calendarEventsData: null, scheduleRequestEventsData: null };
  }
};

const useSafeFilteredAppointmentsV2 = (locationIds: string[]) => {
  try {
    return useGetFilteredAppointmentsV2(locationIds);
  } catch {
    return { appointmentsQuery: { data: null } };
  }
};

export const useGetSelectedCalendarDateAppointmentInfo = (eventId: string): ExtendedAppointment | null => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const { calendarLocationIds } = useCalendarLocationIds();

  // using safe wrappers to handle missing CalendarProvider context
  const { calendarEventsData: calendarEventsV3Data } = useSafeFilteredCalendarEventsV3(calendarLocationIds);
  const {
    appointmentsQuery: { data: appointmentsV2Data },
  } = useSafeFilteredAppointmentsV2(calendarLocationIds);

  const appointment = useMemo<ExtendedAppointment | null>(() => {
    if (isScheduleV3FlagEnabled) {
      const eventInfo = calendarEventsV3Data?.events?.find((e) => e.id === eventId);
      return eventInfo ? convertV3CalendarEventToV2Appointment(eventInfo, locationTimezoneMap) : null;
    }

    return (appointmentsV2Data?.appointments?.find((e) => e.id === eventId) ?? null) as ExtendedAppointment | null;
  }, [
    eventId,
    isScheduleV3FlagEnabled,
    JSON.stringify(calendarEventsV3Data?.events),
    appointmentsV2Data,
    locationTimezoneMap,
  ]);

  return appointment;
};

export const useGetSelectedCalendarDateAppointmentList = (eventIds: string[]): ExtendedAppointment[] => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const { calendarLocationIds } = useCalendarLocationIds();

  // using safe wrappers to handle missing CalendarProvider context
  const { calendarEventsData: calendarEventsV3Data, scheduleRequestEventsData: scheduleRequestEventsV3Data } =
    useSafeFilteredCalendarEventsV3(calendarLocationIds);
  const {
    appointmentsQuery: { data: appointmentsV2Data },
  } = useSafeFilteredAppointmentsV2(calendarLocationIds);

  const appointmentList = useMemo(() => {
    if (isScheduleV3FlagEnabled) {
      const eventsInfo = calendarEventsV3Data?.events.filter((e) => e.id && eventIds.includes(e.id)) || [];
      const requestsInfo =
        scheduleRequestEventsV3Data?.filter((event) => event.eventId && eventIds.includes(event.eventId)) || [];
      const convertedEvents = eventsInfo.map((event) =>
        convertV3CalendarEventToV2Appointment(event, locationTimezoneMap)
      );
      return [...convertedEvents, ...requestsInfo];
    }

    const appointments = appointmentsV2Data?.appointments?.filter((e) => e.id && eventIds.includes(e.id)) || [];
    return appointments as ExtendedAppointment[];
  }, [
    isScheduleV3FlagEnabled,
    calendarEventsV3Data,
    scheduleRequestEventsV3Data,
    appointmentsV2Data,
    eventIds,
    locationTimezoneMap,
  ]);

  return appointmentList;
};
